import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLoginMutation } from '../redux/apiSlice';
import { useDispatch } from 'react-redux';
import { setAccessToken, setRefreshToken } from '../redux/authSlice';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Counter states for stats
  const [counts, setCounts] = useState({
    instructors: 0,
    courses: 0,
    students: 0
  });

  const targetCounts = {
    instructors: 10000,
    courses: 50000,
    students: 1000000
  };

  useEffect(() => {
    setMounted(true);
    
    // Check if remembered email exists in localStorage
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setFormData(prev => ({
        ...prev,
        email: rememberedEmail,
        rememberMe: true
      }));
    }
    
    // Counter animation - 1 minute duration
    const duration = 60000; // 60 seconds = 60000 milliseconds
    const steps = 120; // 120 steps for very smooth animation (every 500ms)
    const interval = duration / steps;
    
    let currentStep = 0;
    
    const counter = setInterval(() => {
      currentStep++;
      
      if (currentStep <= steps) {
        // Use easeOutQuad for smoother deceleration
        const progress = 1 - Math.pow(1 - currentStep / steps, 2);
        
        setCounts({
          instructors: Math.min(Math.floor(targetCounts.instructors * progress), targetCounts.instructors),
          courses: Math.min(Math.floor(targetCounts.courses * progress), targetCounts.courses),
          students: Math.min(Math.floor(targetCounts.students * progress), targetCounts.students)
        });
      } else {
        // Ensure final values are exactly the target
        setCounts({
          instructors: targetCounts.instructors,
          courses: targetCounts.courses,
          students: targetCounts.students
        });
        clearInterval(counter);
      }
    }, interval);
    
    // Cleanup
    return () => clearInterval(counter);
  }, []);

  // Format numbers with commas
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear errors when user starts typing
    if (apiError) {
      setApiError('');
    }
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setApiError('');
    setSuccessMessage('');

    try {
      console.log('📤 Sending login request with:', { email: formData.email });
      
      const credentials = {
        email: formData.email.trim(),
        password: formData.password
      };

      const response = await login(credentials).unwrap();
      
      console.log('✅ Login response received:', response);
      
      if (response.status === true) {
        console.log('🔑 Tokens received - Access:', response.accessToken ? 'Yes' : 'No', 'Refresh:', response.refreshToken ? 'Yes' : 'No');
        
        // Handle "Remember Me" functionality
        if (formData.rememberMe) {
          // Save email to localStorage for auto-fill next time
          localStorage.setItem('rememberedEmail', formData.email.trim());
          console.log('💾 Email saved to localStorage for auto-fill');
        } else {
          // Clear remembered email if "Remember Me" is unchecked
          localStorage.removeItem('rememberedEmail');
          // console.log('🗑️ Email removed from localStorage');
        }
        
        // Store tokens in Redux and localStorage - FIXED: Ensure we're storing token strings
        if (response.accessToken) {
          // Extract token string if it's an object, otherwise use as is
          const accessTokenString = typeof response.accessToken === 'object' ? response.accessToken.token : response.accessToken;
          dispatch(setAccessToken(accessTokenString));
          // console.log('💾 Access token stored in Redux and localStorage');
        }
        
        if (response.refreshToken) {
          // Extract token string if it's an object, otherwise use as is
          const refreshTokenString = typeof response.refreshToken === 'object' ? response.refreshToken.token : response.refreshToken;
          dispatch(setRefreshToken(refreshTokenString));
          // console.log('💾 Refresh token stored in Redux and localStorage');
        }
        
        // console.log('🎉 Login successful!');
        setSuccessMessage(response.message || 'Login successful! Redirecting...');
        
        // Show success message for 2 seconds before redirecting
        setTimeout(() => {
          if (onLogin) {
            onLogin(response);
          }
          // Redirect to dashboard
          navigate('/dashboard', { replace: true });
        }, 2000);

      } else {
        console.warn('⚠️ API returned status false:', response);
        setApiError(response.message || 'Login failed. Please try again.');
      }

    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('🔍 Error details:', {
        status: error.status,
        data: error.data,
        message: error.message
      });
      
      // Handle different error types
      if (error.data) {
        // RTK Query error with response data
        setApiError(error.data.message || 'Login failed. Please check your credentials and try again.');
      } else if (error.status === 401) {
        setApiError('Invalid email or password. Please check your credentials.');
      } else if (error.status === 400) {
        setApiError('Invalid request format. Please check your input.');
      } else if (error.status === 404) {
        setApiError('Service not found. Please try again later.');
      } else if (error.status >= 500) {
        setApiError('Server error. Please try again later.');
      } else if (error.status === 'FETCH_ERROR') {
        setApiError('Network error. Please check your internet connection.');
      } else {
        setApiError('An unexpected error occurred. Please try again.');
      }
    } finally {
      console.log('🏁 Login process completed');
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Function to clear remembered email
  const clearRememberedEmail = () => {
    localStorage.removeItem('rememberedEmail');
    setFormData(prev => ({
      ...prev,
      email: '',
      rememberMe: false
    }));
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Left Side - Visual Section */}
      <div className="lg:flex-1 relative bg-gradient-to-br from-green-500 via-green-600 to-emerald-700 order-2 lg:order-1">
        <div className="absolute inset-0 bg-black bg-opacity-10"></div>
        
        {/* Enhanced Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Subtle Floating Shapes - Centered and smaller */}
          <div className="absolute top-1/4 left-1/4 w-40 h-40 bg-white bg-opacity-10 rounded-full animate-float-slow"></div>
          <div className="absolute top-1/3 right-1/4 w-32 h-32 bg-white bg-opacity-10 rounded-full animate-float-medium"></div>
          <div className="absolute bottom-1/4 left-1/3 w-36 h-36 bg-white bg-opacity-15 rounded-full animate-float-slower"></div>
          
          {/* Subtle Geometric Patterns */}
          <div className="absolute top-20 right-20 w-20 h-20 border-2 border-white border-opacity-15 rounded-xl animate-spin-slow"></div>
          <div className="absolute bottom-20 left-20 w-16 h-16 border border-white border-opacity-10 rounded-lg animate-ping-slow"></div>
        </div>

        {/* Enhanced Content Overlay */}
        <div className="relative h-full flex items-center justify-center p-6 lg:p-12">
          <div className={`text-center text-white max-w-md transform transition-all duration-1000 ease-out ${
            mounted ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {/* Enhanced Logo - Smaller */}
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-md shadow-lg animate-glow">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            
            {/* Enhanced Text Content - Smaller */}
            <h2 className="text-2xl lg:text-3xl xl:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-emerald-100 leading-tight">
              Transform Your Teaching
            </h2>
            
            <p className="text-sm lg:text-base text-emerald-100 mb-6 leading-relaxed font-light">
              Join educators revolutionizing online learning with Pengravity LMS.
            </p>

            {/* Enhanced Stats with Icons - Smaller */}
            <div className="grid grid-cols-3 gap-4 lg:gap-6 mt-8">
              {[
                { value: counts.instructors, label: 'Instructors', icon: '👨‍🏫' },
                { value: counts.courses, label: 'Courses', icon: '📚' },
                { value: counts.students, label: 'Students', icon: '🎓' }
              ].map((stat, index) => (
                <div 
                  key={stat.label}
                  className={`text-center transform transition-all duration-700 ease-out ${
                    mounted ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'
                  }`}
                  style={{ animationDelay: `${index * 200}ms` }}
                >
                  <div className="text-xl mb-1">{stat.icon}</div>
                  <div className="text-lg lg:text-xl font-bold text-white count-number">
                    {formatNumber(stat.value)}+
                  </div>
                  <div className="text-xs text-emerald-100 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col justify-center py-8 lg:py-12 px-6 sm:px-8 lg:px-12 bg-white order-1 lg:order-2">
        <div className="w-full max-w-sm mx-auto">
          {/* Enhanced Logo and Header - Smaller */}
          <div className="text-center mb-8">
            <div className={`flex items-center justify-center space-x-3 mb-6 transform transition-all duration-700 ease-out ${
              mounted ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'
            }`}>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg animate-float-gentle">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-700 bg-clip-text text-transparent">
                  Pengravity
                </h1>
                <p className="text-xs text-gray-500 font-medium">Learning Management System</p>
              </div>
            </div>
            
            <div className={`transform transition-all duration-800 ease-out delay-200 ${
              mounted ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}>
              <h2 className="text-2xl lg:text-3xl font-extrabold text-gray-900 mb-2">
                Welcome Back
              </h2>
              <p className="text-base text-gray-600 font-light">
                Continue your teaching journey
              </p>
            </div>
          </div>

          {/* API Success Message Display */}
          {successMessage && (
            <div className={`mb-6 p-4 bg-green-50 border border-green-200 rounded-xl transform transition-all duration-500 ease-out ${
              mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-green-700 text-sm font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* API Error Display */}
          {apiError && (
            <div className={`mb-6 p-4 bg-red-50 border border-red-200 rounded-xl transform transition-all duration-500 ease-out ${
              mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-700 text-sm font-medium">{apiError}</p>
              </div>
            </div>
          )}

          {/* Enhanced Login Form - Properly sized */}
          <div className={`transform transition-all duration-800 ease-out delay-300 ${
            mounted ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}>
            <form className="space-y-4 lg:space-y-5" onSubmit={handleSubmit}>
              {/* Enhanced Email Field - Smaller */}
              <div className="group">
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-300 bg-white shadow-sm hover:shadow-md pr-12"
                    placeholder="your@email.com"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <svg className="h-5 w-5 text-gray-400 group-focus-within:text-green-500 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                  {/* Clear remembered email button */}
                  {formData.email && localStorage.getItem('rememberedEmail') === formData.email && (
                    <button
                      type="button"
                      onClick={clearRememberedEmail}
                      className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors duration-300"
                      title="Clear remembered email"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
                {localStorage.getItem('rememberedEmail') && formData.email === localStorage.getItem('rememberedEmail') && (
                  <p className="mt-1 text-xs text-green-600 flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Auto-filled from remembered email
                  </p>
                )}
              </div>

              {/* Enhanced Password Field with Toggle */}
              <div className="group">
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-300 bg-white shadow-sm hover:shadow-md pr-12"
                    placeholder="Enter your password"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center space-x-2">
                    {/* Password Toggle Button */}
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="p-1 text-gray-400 hover:text-green-500 transition-colors duration-300 focus:outline-none"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Enhanced Options */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <input
                      id="rememberMe"
                      name="rememberMe"
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      className="w-4 h-4 text-green-500 bg-white border-2 border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:ring-offset-1 transition-all duration-300"
                    />
                  </div>
                  <label htmlFor="rememberMe" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Remember me
                  </label>
                  {formData.rememberMe && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                      ✓ Email will be saved
                    </span>
                  )}
                </div>

                <Link to="/forget-password" className="text-sm font-semibold text-green-600 hover:text-green-700 transition-colors duration-300 hover:underline">
                  Forgot password?
                </Link>
              </div>

              {/* Enhanced Submit Button - Smaller */}
              <button
                type="submit"
                disabled={isLoading || isLoginLoading}
                className="w-full py-3 px-6 text-base font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-200 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none group"
              >
                {(isLoading || isLoginLoading) ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <span>Continue to Dashboard</span>
                    <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                )}
              </button>
            </form>

            {/* Enhanced Sign Up Link - Updated to navigate to /signup */}
            <div className="mt-6 text-center">
              <p className="text-gray-600 text-sm">
                New to Pengravity?{' '}
                <Link to="/signup" className="font-semibold text-green-600 hover:text-green-700 transition-colors duration-300 hover:underline">
                  Create an account
                </Link>
              </p>
            </div>
          </div>
        </div>
        
      
      </div>

      {/* Enhanced Custom CSS */}
      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(180deg); }
        }
        @keyframes float-medium {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-8px) scale(1.02); }
        }
        @keyframes float-slower {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @keyframes float-gentle {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-3px) rotate(3deg); }
          66% { transform: translateY(-2px) rotate(-2deg); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 1; }
          75%, 100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 10px rgba(255, 255, 255, 0.2); }
          50% { box-shadow: 0 0 15px rgba(255, 255, 255, 0.3); }
        }
        @keyframes countUp {
          from { opacity: 0.8; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .count-number {
          animation: countUp 0.1s ease-out;
        }
        .animate-float-slow { animation: float-slow 10s ease-in-out infinite; }
        .animate-float-medium { animation: float-medium 8s ease-in-out infinite; }
        .animate-float-slower { animation: float-slower 12s ease-in-out infinite; }
        .animate-float-gentle { animation: float-gentle 6s ease-in-out infinite; }
        .animate-spin-slow { animation: spin-slow 25s linear infinite; }
        .animate-ping-slow { animation: ping-slow 4s cubic-bezier(0, 0, 0.2, 1) infinite; }
        .animate-glow { animation: glow 4s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default Login;